/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    CALIBRATION_VALUE,
    DEFAULT_SEEK_RANGE_IN_MILLISECONDS,
    HALF_HOUR_IN_MILLISECONDS,
    HOUR_TO_MILLISEC,
    MILLISECONDS_IN_A_SECOND,
    MIN_CACHE_IN_MILLISECONDS,
    PROGRESS_BAR_LENGTH,
    SECONDS_IN_A_MINUTE,
    TICK_DURATION,
} from '../../../config';
import { AppUtils } from '../../AppUtils';
import { DimensionScaler } from '../../device/DimensionScaler';
import { IPlayable } from '../../interfaces';
import { SocuDataModelImpl } from '../../socu/impls';
import { IPlaybackIntervalInfo, ITrickplay } from '../interfaces';

export class TrickplayImpl implements ITrickplay {
    private playable: IPlayable | null;

    private playerData: IPlaybackIntervalInfo;

    private oSocuDataModelImpl = SocuDataModelImpl.getInstance();

    private defaultTickDuration = TICK_DURATION;

    private defaultDuration = HOUR_TO_MILLISEC;

    constructor(playable: IPlayable | null, playerData: IPlaybackIntervalInfo) {
        this.playable = playable;
        this.playerData = playerData;
    }

    /**
     * This function to calculate skip position to perform skip to tick.
     * @returns number
     */
    private calculateSkipToTickPosition(isForwardJump: boolean) {
        let position = 0;
        const isStartOver = this.oSocuDataModelImpl.isStartOver();
        const playerCurrentTime = (this.playerData?.currentTime || 0) / MILLISECONDS_IN_A_SECOND;
        // duration to calculate divisor value in minutes
        const duration = this.getProgramDurationInSeconds() / SECONDS_IN_A_MINUTE;
        const start = (this.playerData?.seekRangeStartTime || 0) / MILLISECONDS_IN_A_SECOND;
        const end = (this.playerData?.seekRangeEndTime || 0) / MILLISECONDS_IN_A_SECOND;
        // currentTimePos will be in seconds for startOver/catchUp, epoch time for live
        const currentTimePos = isStartOver ? playerCurrentTime - start : playerCurrentTime;
        // virtualPos will epoch time reference to convert start over values in epoch time.
        const virtualPos = start;

        position = currentTimePos;
        let remainingTime = isForwardJump ? end - playerCurrentTime : playerCurrentTime - start;
        // add (15, 30, 60) min as per divisor calculation in playback current time
        const timeOffset = this.calculateDivisorFromDurationInMin(duration);
        if (isForwardJump) {
            position += (timeOffset + CALIBRATION_VALUE) * SECONDS_IN_A_MINUTE;
            // round time down to nearest multiply of divisor value, Ex: 17 round down to 15.
            position = AppUtils.roundTimeToNearestQuarterTimeDown(
                position * MILLISECONDS_IN_A_SECOND,
                timeOffset,
            );
        } else {
            position -= (timeOffset + CALIBRATION_VALUE) * SECONDS_IN_A_MINUTE;
            // round time up to nearest multiply of divisor value, Ex: 13 round up to 15.
            position = AppUtils.roundTimeToNearestQuarterTimeUp(
                position * MILLISECONDS_IN_A_SECOND,
                timeOffset,
            );
        }
        position /= MILLISECONDS_IN_A_SECOND;
        // convert absolute time in seconds to epoch timestamp for start over
        position = isStartOver ? virtualPos + position : position;
        position -= playerCurrentTime;
        if (remainingTime < timeOffset * SECONDS_IN_A_MINUTE) {
            remainingTime = Math.floor(remainingTime);
            position = isForwardJump ? remainingTime : -remainingTime;
        }
        return position;
    }

    /**
     * This function to calculate divisor value in minutes from duration
     * @param duration number
     * @returns number
     */
    calculateDivisorFromDurationInMin(duration: number): number {
        const threeHours = 3 * 4 * this.defaultTickDuration;
        const sixHours = 6 * 4 * this.defaultTickDuration;

        if (duration >= this.defaultTickDuration && duration < threeHours) {
            return this.defaultTickDuration;
        }
        if (duration >= threeHours && duration <= sixHours) {
            return 2 * this.defaultTickDuration;
        }
        if (duration > sixHours) {
            return 4 * this.defaultTickDuration;
        }
        return this.defaultTickDuration;
    }

    /**
     * This function to calculate trick-play duration from position in seconds
     * @param position number
     * @param trickPlayTime number
     * @returns number
     */
    calculateTrickplayTimeFromPositionInSeconds(position: number, trickPlayTime: number): number {
        let trickPlayDuration = trickPlayTime;
        const playerCurrentTime = (this.playerData?.currentTime || 0) / MILLISECONDS_IN_A_SECOND;
        const start = (this.playerData?.seekRangeStartTime || 0) / MILLISECONDS_IN_A_SECOND;
        const end = (this.playerData?.seekRangeEndTime || 0) / MILLISECONDS_IN_A_SECOND;
        const actualPos = position + playerCurrentTime;
        if (actualPos >= start && actualPos <= end) {
            if (actualPos > playerCurrentTime) {
                trickPlayDuration += actualPos - playerCurrentTime;
            } else {
                trickPlayDuration += playerCurrentTime - actualPos;
            }
        }
        return trickPlayDuration;
    }

    /**
     * This function to calculate tick mark offset dynamically
     * @param tickMarkCommonOffset number
     * @param index number
     * @param numOfTicks number
     * @returns number
     */
    calculateDynamicTickMarkOffset(
        tickMarkCommonOffset: number,
        index: number,
        numOfTicks: number,
    ): number {
        let offset;
        if (this.oSocuDataModelImpl.isSocu()) {
            const duration = this.getProgramDurationInSeconds() / SECONDS_IN_A_MINUTE;
            const tickOffset =
                DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false) / duration;
            const remainingTime = duration % this.calculateDivisorFromDurationInMin(duration);
            const isRemainingTime = remainingTime !== 0;
            offset =
                index === numOfTicks - 1 && isRemainingTime
                    ? tickMarkCommonOffset +
                      (index * this.calculateDivisorFromDurationInMin(duration) + remainingTime) *
                          tickOffset
                    : tickMarkCommonOffset +
                      (index + 1) * this.calculateDivisorFromDurationInMin(duration) * tickOffset;
        } else {
            const tickOffset =
                DimensionScaler.getInstance().getScaledWidth(PROGRESS_BAR_LENGTH, false) /
                numOfTicks;
            offset = tickMarkCommonOffset + (index + 1) * tickOffset;
        }
        return offset;
    }

    /**
     * This function to calculate program duration in seconds
     * @returns number
     */
    getProgramDurationInSeconds(): number {
        let duration;
        const showingDuration = this.oSocuDataModelImpl.getActionDetails()?.kernel?.duration || 0;
        const durationFromPlayer = (this.playerData?.seekRangeTime || 0) / MILLISECONDS_IN_A_SECOND;
        if (this.oSocuDataModelImpl.isStartOver()) {
            duration = showingDuration > durationFromPlayer ? showingDuration : durationFromPlayer;
        } else if (this.oSocuDataModelImpl.isCatchUp()) {
            duration = durationFromPlayer;
        } else {
            const cacheSize = this.playerData?.seekRangeTime || DEFAULT_SEEK_RANGE_IN_MILLISECONDS;
            const finalCacheSize =
                cacheSize <= MIN_CACHE_IN_MILLISECONDS ? HALF_HOUR_IN_MILLISECONDS : cacheSize;
            // Subtract CACHE_SIZE from the current time and round DOWN to the nearest half.
            let startTime = Date.now() - finalCacheSize;
            startTime =
                Math.floor(startTime / HALF_HOUR_IN_MILLISECONDS) * HALF_HOUR_IN_MILLISECONDS;
            // Start Time plus CACHE_SIZE plus 30 minutes, rounded UP to the nearest half hour.
            let endTime = startTime + finalCacheSize + HALF_HOUR_IN_MILLISECONDS;
            endTime = Math.ceil(endTime / HALF_HOUR_IN_MILLISECONDS) * HALF_HOUR_IN_MILLISECONDS;
            duration = (endTime - startTime) / MILLISECONDS_IN_A_SECOND;
        }
        return duration;
    }

    /**
     * This function to calculate skip position for fast forward
     * @returns number
     */
    getSkipPositionForForward(): number {
        return this.calculateSkipToTickPosition(true);
    }

    /**
     * This function to calculate skip position for rewind
     * @returns number
     */
    getSkipPositionForRewind(): number {
        return this.calculateSkipToTickPosition(false);
    }
}
